// Lightweight history-based router for the back-office.
//
// The app renders pages from a single `active` string (see App.jsx
// renderPage). Detail pages receive a full entity object via `selectedX`
// state. Before this, every reload reset `active` to 'dashboard' — losing
// the user's place. This module syncs `active` (+ the selected entity id)
// to the URL so reload, back/forward, and deep links all work.
//
// Design:
//  - history.state holds a tiny {__nav, active, id} marker (survives reload).
//  - The full entity object is cached in sessionStorage keyed by active+id,
//    so reloading a detail page re-hydrates the exact object it had — no
//    per-page load-by-id needed.
//  - A pasted link in a fresh tab (no cache) degrades to the parent list
//    page rather than crashing a detail view with a partial object.


// active id -> { path, parent? }. Detail paths use :id. `parent` is the list
// surface a detail degrades to when its entity can't be hydrated.
export const ROUTE_TABLE = [
  { active: 'reports-mcp', path: '/reports-mcp' },
  // top-level
  { active: 'dashboard', path: '/' },
  { active: 'events', path: '/events' },
  { active: 'organizers', path: '/organizers' },
  { active: 'event-requests', path: '/event-requests' },
  { active: 'orders', path: '/orders' },
  { active: 'people', path: '/people' },
  { active: 'accreditation', path: '/accreditation' },
  { active: 'users', path: '/users' },
  { active: 'finance', path: '/finance' },
  { active: 'transactions', path: '/transactions' },
  { active: 'split-payments', path: '/split-payments' },
  { active: 'reconciliation', path: '/reconciliation' },
  { active: 'payouts', path: '/payouts' },
  { active: 'funding', path: '/funding' },
  { active: 'wallets', path: '/wallets' },
  { active: 'channels', path: '/channels' },
  { active: 'sales-setup', path: '/sales-setup' },
  { active: 'reports', path: '/reports' },
  { active: 'venues', path: '/venues' },
  { active: 'access-control', path: '/access-control' },
  { active: 'ticketing-inventory', path: '/ticketing-inventory' },
  { active: 'subscriptions', path: '/subscriptions' },
  { active: 'tournaments', path: '/tournaments' },
  { active: 'season-passes', path: '/season-passes' },
  { active: 'products', path: '/products' },
  { active: 'support', path: '/support' },
  { active: 'call-center', path: '/call-center' },
  { active: 'chat', path: '/chat' },
  { active: 'issues-approvals', path: '/issues-approvals' },
  { active: 'design', path: '/design' },
  { active: 'risk', path: '/risk' },
  { active: 'audit', path: '/audit' },
  { active: 'backend-capabilities', path: '/backend-map' },
  { active: 'functionality-reality', path: '/reality-map' },
  { active: 'release-notes', path: '/release-notes' },
  { active: 'settings', path: '/settings' },
  { active: 'utilities', path: '/utilities' },
  { active: 'team', path: '/team' },
  { active: 'health', path: '/health' },
  // detail (most specific first so matching prefers them)
  { active: 'box-office-detail', path: '/channels/box-office/:id', parent: 'channels' },
  { active: 'sales-channel-detail', path: '/channels/channel/:id', parent: 'channels' },
  { active: 'agent-detail', path: '/channels/agent/:id', parent: 'channels' },
  { active: 'team-detail', path: '/tournaments/team/:id', parent: 'tournaments' },
  { active: 'tournament-detail', path: '/tournaments/:id', parent: 'tournaments' },
  { active: 'event-detail', path: '/events/:id', parent: 'events' },
  { active: 'organizer-statement', path: '/organizers/:id/statement', parent: 'organizers' },
  { active: 'organizer-detail', path: '/organizers/:id', parent: 'organizers' },
  { active: 'request-review', path: '/event-requests/:id', parent: 'event-requests' },
  { active: 'order-detail', path: '/orders/:id', parent: 'orders' },
  // The id here is the ORDER uuid — an arrangement has no identity of its own, it IS an order
  // being collected in parts. Deliberately its own path rather than a tab on the order page: the
  // arrangement is what an operator is looking after, and it must be linkable on its own.
  { active: 'arrangement-detail', path: '/split-payments/:id', parent: 'split-payments' },
  // One event's split setup. A distinct path from the core Events module's /events/:id — that page
  // carries the event record; this one carries only its split-payment relationship.
  { active: 'event-splits', path: '/split-payments/event/:id', parent: 'split-payments' },
  { active: 'customer-detail', path: '/customers/:id', parent: 'people' },
  // A guest is a PERSON, so they get an address. Their invitation history, credential and reply
  // lived inside an edit dialog, which meant none of it could be linked to, shared with the
  // person chasing the RSVP, or reloaded — the whole record vanished on Escape.
  { active: 'guest-detail', path: '/accreditation/guest/:id', parent: 'accreditation' },
  { active: 'transaction-detail', path: '/transactions/:id', parent: 'transactions' },
  { active: 'payout-detail', path: '/payouts/:id', parent: 'payouts' },
  { active: 'event-settlement-detail', path: '/settlements/:id', parent: 'payouts' },
  { active: 'venue-detail', path: '/venues/:id', parent: 'venues' },
  // Chat has no separate list/detail component (one page renders both the
  // conversation list and the open thread) — this just puts the SELECTED
  // conversation in the URL so it survives refresh AND can be shared/
  // bookmarked, the same as every other detail route already does. A plain
  // sessionStorage key (the first version of this) only solved "survives
  // refresh in this tab" — pasting the link to a colleague landed them on an
  // empty list, not the conversation being discussed.
  { active: 'chat-thread', path: '/chat/:id', parent: 'chat' },
];

const BY_ACTIVE = Object.fromEntries(ROUTE_TABLE.map((r) => [r.active, r]));

// Compile a /a/b/:id template into a matcher. More segments and fewer params
// rank higher so detail routes win over their list parents.
const compile = (route) => {
  const segs = route.path.split('/').filter(Boolean);
  const params = segs.filter((s) => s.startsWith(':')).length;
  const re = new RegExp('^/' + segs.map((s) => (s.startsWith(':') ? '([^/]+)' : s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'))).join('/') + '/?$');
  return { route, re, hasParam: params > 0, score: segs.length * 10 - params };
};
const MATCHERS = ROUTE_TABLE.map(compile).sort((a, b) => b.score - a.score);

// `routeId` lets an entity be addressed by a stable, backend-resolvable id (e.g.
// orders/transactions route by the order UUID, not the human reference on `.id`).
export const entityId = (entity) => (entity && (entity.routeId || entity.id || entity.code)) || '';

export const pathForRoute = (active, entity) => {
  const route = BY_ACTIVE[active];
  if (!route) return '/';
  if (route.path.includes(':id')) {
    const id = entityId(entity);
    return id ? route.path.replace(':id', encodeURIComponent(id)) : (BY_ACTIVE[route.parent]?.path || '/');
  }
  return route.path;
};

// The list route a detail route belongs to ('order-detail' → 'orders'), and a human name
// for the thing it addresses. Used by the not-found state so a dead deep link can say what
// it failed to find and offer the way back, instead of silently rendering the parent list.
export const parentOfRoute = (active) => BY_ACTIVE[active]?.parent || null;
export const labelForRoute = (active) =>
  (active || '').replace(/-detail$/, '').replace(/-/g, ' ') || 'record';

// pathname -> { active, id } (id is the raw URL segment, no cached object yet)
export const parsePathname = (pathname) => {
  const clean = (pathname || '/').replace(/\/+$/, '') || '/';
  for (const m of MATCHERS) {
    const match = clean.match(m.re);
    if (match) return { active: m.route.active, id: m.hasParam ? decodeURIComponent(match[1]) : null };
  }
  return { active: 'dashboard', id: null };
};

// --- entity cache (sessionStorage) so reload re-hydrates the full object ----
const cacheKey = (active, id) => `nav:${active}:${id}`;

// The cache is a STORE, and a store that changes silently can only be read by whoever
// happens to re-render. `cacheEntity` is the single write, so it is also the single
// notification — that is what lets the page title be derived rather than assigned.
const cacheSubscribers = new Set();
export const onEntityCached = (fn) => { cacheSubscribers.add(fn); return () => cacheSubscribers.delete(fn); };

export const cacheEntity = (active, entity) => {
  const id = entityId(entity);
  if (!id) return;
  try { window.sessionStorage.setItem(cacheKey(active, id), JSON.stringify(entity)); } catch { /* quota / serialization — non-fatal */ }
  cacheSubscribers.forEach((fn) => fn(active, id));
};
export const readCachedEntity = (active, id) => {
  if (!id) return null;
  try { const raw = window.sessionStorage.getItem(cacheKey(active, id)); return raw ? JSON.parse(raw) : null; } catch { return null; }
};
// NOTE: TanStack Router now owns URL/history (see src/router.tsx + App.jsx).
// This module is reduced to pure helpers: ROUTE_TABLE, parsePathname,
// pathForRoute, and the sessionStorage entity cache (cacheEntity /
// readCachedEntity) used to rehydrate a detail object on same-session reload.
// The old useUrlRouting/getInitialNav/resolveNav history machinery was removed.
